import type {
  PublisherProvider,
  Subscription,
  SubscriptorProvider,
  ToolbarType,
} from './../ricos-types';

type Topics = [
  'ricos.plugins.functionality.pluginAddSuccess',
  'ricos.plugins.functionality.pluginAdd',
  'ricos.plugins.functionality.pluginDelete',
  'ricos.plugins.functionality.pluginToolbarButtonClick',
  'ricos.plugins.functionality.pluginPopoverClick',
  'ricos.plugins.functionality.pluginPopoverTabSwitch',
  'ricos.plugins.functionality.pluginPopoverSearch',
  'ricos.plugins.functionality.pluginChangeSettings',
  'ricos.plugins.functionality.pluginLinkable',
  'ricos.plugins.functionality.mediaPluginsDetails',
  'ricos.plugins.table.addColumnRow',
  'ricos.plugins.table.deleteColumnRow',
  'ricos.plugins.table.contextMenuClick',
  'ricos.plugins.table.contextMenuOptionClick',
  'ricos.plugins.sideEffect.layoutShift',
  'ricos.plugins.functionality.modalAction'
];

const LAYOUT_SHIFT_TOPIC = 'ricos.plugins.sideEffect.layoutShift';

const TOPICS: Topics = [
  'ricos.plugins.functionality.pluginAddSuccess',
  'ricos.plugins.functionality.pluginAdd',
  'ricos.plugins.functionality.pluginDelete',
  'ricos.plugins.functionality.pluginToolbarButtonClick',
  'ricos.plugins.functionality.pluginPopoverClick',
  'ricos.plugins.functionality.pluginPopoverTabSwitch',
  'ricos.plugins.functionality.pluginPopoverSearch',
  'ricos.plugins.functionality.pluginChangeSettings',
  'ricos.plugins.functionality.pluginLinkable',
  'ricos.plugins.functionality.mediaPluginsDetails',
  'ricos.plugins.table.addColumnRow',
  'ricos.plugins.table.deleteColumnRow',
  'ricos.plugins.table.contextMenuClick',
  'ricos.plugins.table.contextMenuOptionClick',
  LAYOUT_SHIFT_TOPIC,
  'ricos.plugins.functionality.modalAction',
];

// ✓  onPluginAction --> table left
// ✓  onChangePluginSettings
// ✓  onPluginAdd --> toolbar button click
// ✓  onPluginAddStep --> modal open
// ✓  onPluginAddSuccess
// ☐  onPluginChange
// ✓  onPluginDelete
// ✓  onPluginsPopOverClick
// ✓  onPluginsPopOverTabSwitch
// ✓  onToolbarButtonClick --> plugin toolbar button click (includes value)
// ✓  mediaPluginsDetails
// ☐  onVideoSelected --> ?

export type IPluginsEvents = PluginsEvents;

export class PluginsEvents {
  publishers!: PublisherProvider<Topics>;

  subscriptors!: SubscriptorProvider<[typeof LAYOUT_SHIFT_TOPIC]>;

  topicsToPublish = TOPICS;

  topicsToSubscribe = [LAYOUT_SHIFT_TOPIC];

  publishPluginAddSuccess({
    pluginId,
    params,
  }: {
    pluginId: string;
    params?: Record<string, any>;
  }) {
    return this.publishers
      .byTopic('ricos.plugins.functionality.pluginAddSuccess')
      .publish({ pluginId, params });
  }

  publishPluginAdd({
    pluginId,
    entryPoint,
  }: {
    pluginId: string;

    entryPoint: string;
  }) {
    return this.publishers
      .byTopic('ricos.plugins.functionality.pluginAdd')
      .publish({ pluginId, entryPoint });
  }

  publishPluginDelete({ pluginId }: { pluginId: string }) {
    return this.publishers
      .byTopic('ricos.plugins.functionality.pluginDelete')
      .publish({ pluginId });
  }

  publishPluginToolbarClick({
    pluginId,
    buttonName,
    value,
    nodeId,
    type,
  }: {
    pluginId: string;
    buttonName: string;
    nodeId: string;
    value?: string;
    type?: ToolbarType;
  }) {
    return this.publishers
      .byTopic('ricos.plugins.functionality.pluginToolbarButtonClick')
      .publish({ pluginId, buttonName, value, nodeId, type: type ?? 'PLUGIN' });
  }

  publishPluginPopoverClick({ pluginId, buttonName }: { pluginId: string; buttonName: string }) {
    return this.publishers
      .byTopic('ricos.plugins.functionality.pluginPopoverClick')
      .publish({ pluginId, buttonName });
  }

  publishPluginPopoverTabSwitch({
    pluginId,
    buttonName,
  }: {
    pluginId: string;
    buttonName: string;
  }) {
    return this.publishers
      .byTopic('ricos.plugins.functionality.pluginPopoverTabSwitch')
      .publish({ pluginId, buttonName });
  }

  publishPluginPopoverSearch({ pluginId, searchTerm }: { pluginId: string; searchTerm: string }) {
    return this.publishers
      .byTopic('ricos.plugins.functionality.pluginPopoverSearch')
      .publish({ pluginId, searchTerm });
  }

  publishPluginChangeSettings({
    pluginId,
    nodeId,
    actionName,
    value,
  }: {
    pluginId: string;
    nodeId: string;
    actionName: string;
    value: string;
  }) {
    return this.publishers
      .byTopic('ricos.plugins.functionality.pluginChangeSettings')
      .publish({ pluginId, nodeId, actionName, value });
  }

  publishPluginLinkable({
    pluginId,
    nodeId,
    category,
    link,
    nofollow,
    noreferrer,
    newTab,
    anchor,
  }: {
    pluginId: string;
    nodeId: string;
    link?: string;
    category?: string;
    nofollow?: boolean;
    noreferrer?: boolean;
    newTab?: boolean;
    anchor?: string;
  }) {
    return this.publishers
      .byTopic('ricos.plugins.functionality.pluginLinkable')
      .publish({ pluginId, nodeId, category, link, nofollow, noreferrer, newTab, anchor });
  }

  publishModalAction({
    pluginId,
    actionName,
    value,
    modalSessionId,
    modalId,
    actionNumber,
    initialSelection,
    style,
  }: {
    pluginId: string;
    actionName?: string;
    modalId: string;
    value?: string;
    modalSessionId: string;
    actionNumber: number;
    initialSelection?: string;
    style?: string;
  }) {
    return this.publishers.byTopic('ricos.plugins.functionality.modalAction').publish({
      pluginId,
      actionName,
      value,
      modalSessionId,
      modalId,
      actionNumber,
      initialSelection,
      style,
    });
  }

  publishTableAddRowCol({
    pluginId,
    nodeId,
    category,
    location,
    source,
  }: {
    pluginId: string;
    nodeId: string;
    category: string;
    location: string;
    source: string;
  }) {
    return this.publishers
      .byTopic('ricos.plugins.table.addColumnRow')
      .publish({ pluginId, nodeId, category, location, source });
  }

  publishTableContextMenuClick({
    pluginId,
    nodeId,
  }: {
    pluginId: string;
    nodeId: string;
    category: string;
  }) {
    return this.publishers
      .byTopic('ricos.plugins.table.contextMenuClick')
      .publish({ pluginId, nodeId });
  }

  publishTableDeleteColumnRow({
    pluginId,
    nodeId,
    category,
  }: {
    pluginId: string;
    nodeId: string;
    category: string;
  }) {
    return this.publishers
      .byTopic('ricos.plugins.table.deleteColumnRow')
      .publish({ pluginId, nodeId, category });
  }

  publishTableContextMenuOptionClick({
    pluginId,
    nodeId,
    category,
    actionName,
  }: {
    pluginId: string;
    nodeId: string;
    category: string;
    actionName: string;
  }) {
    return this.publishers
      .byTopic('ricos.plugins.table.contextMenuOptionClick')
      .publish({ pluginId, nodeId, category, actionName });
  }

  publishMediaPluginsDetails({
    pluginId,
    nodeId,
    creator,
    title,
    track_duration,
    type,
    url,
  }: {
    pluginId: string;
    nodeId: string;
    creator?: string;
    title?: string;
    track_duration?: string;
    type: 'File Upload' | 'Spotify' | 'SoundCloud';
    url: string;
  }) {
    return this.publishers
      .byTopic('ricos.plugins.functionality.mediaPluginsDetails')
      .publish({ pluginId, nodeId, creator, title, track_duration, type, url });
  }

  publishPluginLayoutShift({ nodeId }: { nodeId: string }) {
    return this.publishers.byTopic(LAYOUT_SHIFT_TOPIC).publish({ nodeId });
  }

  onPluginLayoutShift(callback: () => void): Subscription {
    return this.subscriptors.byTopic(LAYOUT_SHIFT_TOPIC).subscribe(callback);
  }
}
